import { useMemo } from 'react'
import { Download, FolderPlus, Plus, Settings, Upload } from 'lucide-react'
import { TeamSelector } from './TeamSelector'
import { HEADER_HEIGHT } from '@/constants'
import { IconButton } from '../ui/IconButton'
import { ViewToggle } from '../ui/ViewToggle'
import { SearchInput } from '../ui/SearchInput'
import { TooltipProvider } from '../ui/Tooltip'
import type { SectionId } from './ContentSidebar'
import './content-toolbar.css'

type Team = {
	id: string
	name: string
	role: 'owner' | 'editor' | 'viewer'
}

type ToolbarProps = {
	activeSection: SectionId
	selectedFolderName?: string | null
	viewMode: 'grid' | 'list'
	onViewModeChange: (mode: 'grid' | 'list') => void
	searchQuery: string
	onSearchChange: (query: string) => void
	onNewPlaybook: () => void
	onNewDocument: () => void
	onNewFolder: () => void
	onImport: () => void
	onExport: () => void
	onSettingsClick: () => void
	teams: Team[]
	currentTeamId: string | null
	onSwitchTeam: (teamId: string) => void
	onManageTeams: () => void
}

export function ContentToolbar({
								   activeSection,
								   selectedFolderName,
								   viewMode,
								   onViewModeChange,
								   searchQuery,
								   onSearchChange,
								   onNewPlaybook,
								   onNewDocument,
								   onNewFolder,
								   onImport,
								   onExport,
								   onSettingsClick,
								   teams,
								   currentTeamId,
								   onSwitchTeam,
								   onManageTeams,
							   }: ToolbarProps) {
	const searchPlaceholder = useMemo(() => {
		if (selectedFolderName) return `Search in ${selectedFolderName}...`
		switch (activeSection) {
			case 'playbooks': return 'Search playbooks...'
			case 'documents': return 'Search documents...'
			case 'starred': return 'Search starred...'
			case 'trash': return 'Search trash...'
			default: return 'Search...'
		}
	}, [activeSection, selectedFolderName])

	const newButtonConfig = useMemo(() => {
		switch (activeSection) {
			case 'playbooks':
				return {
					label: 'New Playbook',
					onClick: onNewPlaybook,
				}
			case 'documents':
				return {
					label: 'New Document',
					onClick: onNewDocument,
				}
			default:
				return {
					label: 'New Playbook',
					onClick: onNewPlaybook,
				}
		}
	}, [activeSection, onNewPlaybook, onNewDocument])

	return (
		<TooltipProvider>
			<div className="content-toolbar" style={{ height: `${HEADER_HEIGHT}px` }}>
				<div className="content-toolbar-container">
					<div className="content-toolbar-content">
						{/* Left Section - Search */}
						<SearchInput
							value={searchQuery}
							onChange={onSearchChange}
							placeholder={searchPlaceholder}
							style={{ flex: 1, maxWidth: '70rem' }}
							data-tour-id="search-input"
						/>

						{/* Right Section - Actions */}
						<div className="content-toolbar-actions">
							{/* Team Selector */}
							<TeamSelector
								teams={teams}
								currentTeamId={currentTeamId}
								onSwitchTeam={onSwitchTeam}
								onManageTeams={onManageTeams}
							/>

							<div className="content-toolbar-divider" />

							{/* View Mode Toggle */}
							<ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange}
										data-tour-id="view-toggle" />

							<div className="content-toolbar-divider" />

							{/* Action Buttons */}
							<button
								onClick={newButtonConfig.onClick}
								className="content-toolbar-new-playbook"
								data-tour-id="new-button"
							>
								<Plus className="content-toolbar-icon" />
								{newButtonConfig.label}
							</button>

							<IconButton
								icon={FolderPlus}
								tooltip="New Folder"
								onClick={onNewFolder}
								variant="ghost"
							/>

							<IconButton
								icon={Upload}
								tooltip="Import"
								variant="ghost"
								onClick={onImport}
							/>

							<IconButton
								icon={Download}
								tooltip="Export"
								variant="ghost"
								onClick={onExport}
							/>

							<div className="content-toolbar-divider" />

							{/* Settings */}
							<IconButton
								icon={Settings}
								tooltip="Settings"
								variant="ghost"
								onClick={onSettingsClick}
							/>
						</div>
					</div>
				</div>
			</div>
		</TooltipProvider>
	)
}
